import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserCircle, Send } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { selfAPI } from '../services/api';

const FIELD_LABELS = {
  mobile_number: 'Mobile Number',
  email: 'Email Address',
  pan: 'PAN',
  aadhaar: 'Aadhaar',
};

export default function EmployeeProfile() {
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ fieldName: 'mobile_number', proposedValue: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const load = () => Promise.all([
    selfAPI.profile(),
    selfAPI.profileCorrectionRequests(),
  ]).then(([profRes, reqRes]) => {
    setProfile(profRes.data);
    setRequests(reqRes.data || []);
  });

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await selfAPI.submitProfileCorrection(form);
      setMessage(data.message);
      setForm({ fieldName: form.fieldName, proposedValue: '', reason: '' });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this correction request?')) return;
    await selfAPI.cancelProfileCorrection(id);
    await load();
  };

  if (loading) return <LoadingSpinner />;
  if (!profile) return <p className="text-slate-500">Profile not found.</p>;

  return (
    <div className="space-y-6 max-w-3xl">
      <Link to="/" className="text-primary-600 text-sm">← Back to My Coverage</Link>
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <UserCircle className="w-7 h-7 text-primary-600" />
          My Profile
        </h1>
        <p className="text-slate-500 mt-1">View your details and request KYC corrections</p>
      </div>

      {message && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">{message}</div>
      )}

      <div className="card grid sm:grid-cols-2 gap-4 text-sm">
        <div><span className="text-slate-400">Name</span><p className="font-medium">{profile.full_name}</p></div>
        <div><span className="text-slate-400">Employee ID</span><p>{profile.employee_code}</p></div>
        <div><span className="text-slate-400">Email</span><p>{profile.email}</p></div>
        <div><span className="text-slate-400">Mobile</span><p>{profile.mobile_number || '—'}</p></div>
        <div><span className="text-slate-400">Department</span><p>{profile.department_name || '—'}</p></div>
        <div><span className="text-slate-400">Aadhaar</span><p>{profile.aadhaar_masked || '—'}</p></div>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-4">
        <h2 className="font-semibold flex items-center gap-2"><Send className="w-4 h-4" />Request Profile Correction</h2>
        <p className="text-sm text-slate-500">Submit a correction for HR review. Approved changes update your official records.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Field to correct *</label>
            <select className="input-field" value={form.fieldName} onChange={(e) => setForm({ ...form, fieldName: e.target.value })}>
              {Object.entries(FIELD_LABELS).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">New value *</label>
            <input className="input-field" required value={form.proposedValue}
              onChange={(e) => setForm({ ...form, proposedValue: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label">Reason *</label>
          <textarea className="input-field min-h-[80px]" required value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </div>
        {error && <p className="text-red-600 text-sm">{error}</p>}
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit Correction Request'}
        </button>
      </form>

      {requests.length > 0 && (
        <div className="card">
          <h2 className="font-semibold mb-3">Correction Requests</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-500 border-b text-left">
                <th className="pb-2">Request</th><th className="pb-2">Field</th><th className="pb-2">Status</th><th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} className="border-b border-slate-100">
                  <td className="py-2 font-medium">{r.request_number}</td>
                  <td>{FIELD_LABELS[r.field_name] || r.field_name}</td>
                  <td className="capitalize">{r.status}</td>
                  <td>
                    {r.status === 'pending' && (
                      <button type="button" onClick={() => handleCancel(r.id)} className="text-red-600 text-xs hover:underline">
                        Cancel
                      </button>
                    )}
                    {r.review_remarks && r.status === 'rejected' && (
                      <span className="text-slate-500 text-xs block">{r.review_remarks}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
